import type { LandingStatus } from '@/modules/cleepo/interfaces/landing.interface';
import type { ConfigurationTask } from './configurationTask.interface';
import type { Domain } from './domain.interface';

export enum MarketingPlanStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ERROR = 'ERROR',
  CANCELLED = 'CANCELLED',
}

export enum ProjectStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  PENDING = 'PENDING',
  INACTIVE = 'INACTIVE',
  ERROR = 'ERROR',
}

export enum ProjectBriefStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  CHANGES_REQUESTED = 'CHANGES_REQUESTED',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
}

export interface Project {
  id: number;
  name: string;
  description?: string;
  status: ProjectStatus;
  account_id: number;
  order_id: number;
  owner_id: number;
  solution_id: number;
  country_id: number;
  plan_id: number;
  onboarding_transcript?: string;
  brief?: string;
  brief_summary?: string;
  is_migrated: number;
  brief_status?: ProjectBriefStatus;
  log?: string;
  created_at: string;
  updated_at: string;
  settings?: ProjectSettings;
  brief_comments?: string;
  latest_project_monitor?: LatestProjectMonitor;
  landkit_integration?: ProjectLandkitIntegration;
  google_ads_integration?: ProjectGoogleAdsIntegration;
  domain_integration?: ProjectDomainIntegration;
  configuration_task_summaries?: ConfigurationTask[];
}

export interface ProjectDomainIntegration {
  id: number;
  project_id: number;
  domain_id: number;
  status: string;
  log?: string | null;
  created_at: string;
  updated_at: string;
  domain: Domain;
}

export enum ProjectSettingsPlanType {
  PAID = 'PAID',
  TRIAL = 'TRIAL',
}
export interface ProjectSettings {
  id: number;
  cleepo_plan_type: ProjectSettingsPlanType;
  cleepo_plan_name: string;
  project_id: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectLandkitIntegration {
  id: number;
  project_id: number;
  landing_id: number;
  status: ProjectLandkitIntegrationStatus;
  external_status: LandingStatus;
  main_publication_url?: string;
  logo_url?: string;
  sync_status?: ProjectIntegrationSyncStatus;
  last_sync_at?: string;
  log?: string;
  created_at: string;
  updated_at: string;
}

export interface ProjectGoogleAdsIntegration {
  id: number;
  project_id: number;
  marketing_plan_id: number;
  status: string;
  external_status: MonitorGaCampaignStatus;
  sync_status?: ProjectIntegrationSyncStatus;
  last_sync_at?: string;
  log?: string | null;
  created_at: string;
  updated_at: string;
  budget_amount_micros?: number;
  campaign_gid?: string;
  campaign_name?: string;
  campaigns?: unknown[];
  customer_gid?: string;
}

export interface LatestProjectMonitor {
  project_id: number;
  for_date: string;
  landing_id: number;
  landing_status?: LandingStatus;
  landing_visits?: number;
  landing_clicks?: number;
  landing_whatsapp?: number;
  landing_calls?: number;
  landing_leads?: number;
  landing_potentials?: number;
  landing_visits_score: number;
  landing_conversions_score: number;
  landing_score: number;
  landing_status_score?: MonitorLandingStatusScore;
  landing_has_domain: boolean;
  landing_domain_status?: MonitorLandingDomainStatus;
  landing_domain_expires_at?: string;
  marketing_plan_id?: number;
  ga_customer_id?: number;
  ga_campaign_id?: number;
  ga_campaign_status?: MonitorGaCampaignStatus;
  ga_campaign_primary_status?: string;
  ga_campaign_primary_status_reasons?: string;
  ga_campaign_optimization_score?: number;
  ga_campaign_target_cpc_bid_ceiling_micros?: number;
  ga_campaign_serving_status?: string;
  ga_campaign_bidding_strategy_type?: string;
  ga_campaign_bidding_strategy_system_status?: string;
  ga_campaign_advertising_channel_type?: string;
  ga_budget_id?: number;
  ga_budget_amount_micros?: number;
  ga_metrics_cost_micros: number;
  ga_metrics_impressions: number;
  ga_metrics_clicks: number;
  ga_metrics_conversions: number;
  ga_metrics_ctr?: number;
  ga_metrics_average_cpc?: number;
  ga_metrics_cost_per_conversion?: number;
  ga_metrics_average_cost?: number;
  message_errors?: string;
}

export enum MonitorLandingStatusScore {
  OK = 'OK',
  LOW_CONVERSIONS = 'LOW_CONVERSIONS',
  LOW_VISITS = 'LOW_VISITS',
  LOW_VISITS__LOW_CONVERSIONS = 'LOW_VISITS__LOW_CONVERSIONS',
}

export enum MonitorGaCampaignStatus {
  LEARNING = 'LEARNING',
  ELIGIBLE = 'ELIGIBLE',
  ELIGIBLE_LIMITED = 'ELIGIBLE (LIMITED)',
  LIMITED = 'LIMITED',
  ENABLED = 'ENABLED',
  PENDING = 'PENDING',
  PAUSED = 'PAUSED',
  REMOVED = 'REMOVED',
  ENDED = 'ENDED',
  MISCONFIGURED = 'MISCONFIGURED',
  NOT_ELIGIBLE = 'NOT_ELIGIBLE',
}

export enum MonitorLandingDomainStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  EXPIRED = 'EXPIRED',
  AWAITING = 'AWAITING',
}

export enum ProjectLandkitIntegrationStatus {
  DRAFT = 'DRAFT',
  BUILDING = 'BUILDING',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ERROR = 'ERROR',
  CANCELLED = 'CANCELLED',
}

export enum ProjectIntegrationSyncStatus {
  PENDING = 'PENDING',
  ERROR = 'ERROR',
  SYNCHRONIZED = 'SYNCHRONIZED',
}
